import type { Dispatch, MapDispatchToProps, MapStateToProps, MergeProps } from 'react-redux';
import { change, type FormAction, type FormStateMap } from 'redux-form';
import { connect } from 'react-redux';
import type { QueryResult } from '@apollo/client';
import type {
  GetAncillaryItemsOnBookingStepQuery,
  GetAncillaryItemsQuery,
} from '@eti/schema-types';
import type { SupportedProductsType } from '../../../../common/utils/models';
import { CONTACT_INFO_FORM_SECTION, contactFields } from '../../constants/formFields';
import { getAddedExtraProducts } from '../../../../utils/addedExtraProducts';
import { TRAVELER_DETAILS } from '../../../../constants/formNamesConstants';
import type { CustomerPhoneNumberDetailsType } from '../../models';
import { PhonePrefixChangeConfirmationDialog } from './PhonePrefixChangeConfirmationDialog';

interface CGProductQueryVars extends Exclude<CustomerPhoneNumberDetailsType, null> {
  productIds: string[];
}

interface GetCGProductsFn {
  (options: {
    variables: CGProductQueryVars;
  }): Promise<QueryResult<GetAncillaryItemsOnBookingStepQuery | GetAncillaryItemsQuery>> | void;
}

interface OwnProps {
  getCGProducts: GetCGProductsFn;
  phoneNumber: CustomerPhoneNumberDetailsType;
  coverGeniusProductIds: number[];
  fetchedCGProducts?: GetAncillaryItemsOnBookingStepQuery['ancillaryItems'];
}

interface FormState {
  form: FormStateMap;
}

interface DispatchProps {
  dispatch: Dispatch<FormAction>;
  updateReduxPhoneAndPrefix: (value: Exclude<CustomerPhoneNumberDetailsType, null>) => void;
}

interface ConnectedStateProps {
  isCgProductAddedInCart: boolean;
  productsAddedInCart: SupportedProductsType;
}

interface PhonePrefixChangeConfirmationDialogProps
  extends OwnProps,
    ConnectedStateProps,
    DispatchProps {}

export interface ConnectedMergeProps extends PhonePrefixChangeConfirmationDialogProps {
  removeCgProductFromCart: (
    productsId?: GetAncillaryItemsOnBookingStepQuery['ancillaryItems'],
  ) => void;
}

const mapStateToProps: MapStateToProps<ConnectedStateProps, OwnProps, FormState> = (
  state: FormState,
  { coverGeniusProductIds }: { coverGeniusProductIds: number[] },
) => {
  const productsAddedInCart = getAddedExtraProducts(TRAVELER_DETAILS)(
    state,
  ) as SupportedProductsType;

  const isCgProductAddedInCart = productsAddedInCart.some((product) => {
    return coverGeniusProductIds.includes(product.id);
  });

  return {
    productsAddedInCart,
    isCgProductAddedInCart,
  };
};

const mapDispatchToProps: MapDispatchToProps<DispatchProps, OwnProps> = (dispatch) => {
  return {
    dispatch,
    updateReduxPhoneAndPrefix: ({
      customerPhoneNumber,
      customerPhoneNumberPrefix,
    }: Exclude<CustomerPhoneNumberDetailsType, null>) => {
      dispatch(
        change(
          TRAVELER_DETAILS,
          `${CONTACT_INFO_FORM_SECTION}.${contactFields.PHONE}`,
          customerPhoneNumber,
        ),
      );
      dispatch(
        change(
          TRAVELER_DETAILS,
          `${CONTACT_INFO_FORM_SECTION}.${contactFields.PHONE_COUNTRY_ID}`,
          customerPhoneNumberPrefix,
        ),
      );
    },
  };
};

const mergeProps: MergeProps<ConnectedStateProps, DispatchProps, OwnProps, ConnectedMergeProps> = (
  state: ConnectedStateProps,
  action: DispatchProps,
  ownProps: OwnProps,
) => {
  return {
    ...ownProps,
    ...state,
    ...action,
    removeCgProductFromCart: (fetchedCGProducts) => {
      const fetchedCGProductIds = fetchedCGProducts?.map(({ id }) => Number(id)) ?? [];

      state.productsAddedInCart.forEach((product) => {
        const isInFetchedCGProducts = fetchedCGProductIds.includes(product.id);
        const isCGProduct = ownProps.coverGeniusProductIds.includes(product.id);

        if (isCGProduct && !isInFetchedCGProducts) {
          action.dispatch(change(TRAVELER_DETAILS, `${product.name}.value.value`, null));
        }
      });
    },
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(PhonePrefixChangeConfirmationDialog);
